{ pitchBendToFloat, panToFloat, floatToPan } from ./util.civet

type { MidiEvent } from ./event.civet

microsecondsPerSecond := 1000000

/**
 * Channel holds data for a single MIDI channel at a point in time
 * pitch bend, continuous control values, program, tuning, etc.
 */
class Channel
  id: ChannelID
  control = new Uint8Array(120)
  program = 0
  /**  float [-1, 1] */
  pitchBend = 0
  /** cents */
  pitchBendSensitivity = 200
  /** -/+ cents */
  tuningCoarse = 0
  /** -/+ cents */
  tuningFine = 0
  /**
  Which register the next data entry (CC 6/38) writes: CC 98/99 select NRPN, CC 100/101
  select RPN. Without this a file that sets pitch-bend range and then writes vibrato
  NRPNs would smash its bend range with vibrato values.
  */
  dataMode: "rpn" | "nrpn" = "rpn"
  /**
  GS sound-controller NRPN offsets, each -64..63 with 0 = "as the patch says" (the wire
  value 64 is neutral). Consumed by the synth at noteOn as adjustments to the zone's own
  generators.
  */
  nrpn = neutralNrpn()
  /**
  GS drum-setup NRPNs (MSB 0x18-0x1E), keyed by drum note number. `pitch` is a semitone
  offset around 64; `level`/`reverb`/`chorus`/`pan` are the raw 0-127 wire values.
  ZUN's sequences lean on these — Septette drops its kick 5 semitones and boosts the
  snare to 120, which is most of what "boomy" means on real Sound Canvas hardware.
  */
  drumNrpn: Record<number, {pitch?: number, level?: number, pan?: number, reverb?: number, chorus?: number, delay?: number}> = {}

  shared: ChannelData

  @({@id, @shared}: {id: ChannelID, shared: ChannelData})

  get bank
    @control[0]
  set bank(v: number)
    @control[0] = v & 0x7F

  get expression
    @control[11] / 127
  set expression(v: number)
    @control[11] = v * 127

  get fx1
    @control[91] / 127
  set fx1(v)
    @control[91] = v * 127

  get fx2
    @control[92] / 127
  set fx2(v)
    @control[92] = v * 127

  get fx3
    @control[93] / 127
  set fx3(v)
    @control[93] = v * 127

  get fx4
    @control[94] / 127
  set fx4(v)
    @control[94] = v * 127

  get fx5
    @control[95] / 127
  set fx5(v)
    @control[95] = v * 127

  /**
  * gain amplitude is pow(volume * expression, 2)
  * https://music.stackexchange.com/a/54909/36077
  */
  get gain
    v := @volume
    e := @expression

    return v * v * e * e

  get mod
    @control[1] / 127
  set mod(v)
    @control[1] = v * 127

  get pan
    panToFloat @control[10]
  set pan(v: number)
    @control[10] = floatToPan v

  get sustain
    //@ts-ignore TODO
    @control[64] >= 64
  set sustain(v: number)
    if v
      @control[64] = 127
    else
      @control[64] = 0

  /** cents */
  get tuning
    @tuningCoarse + @tuningFine

  get volume
    @control[7] / 127
  set volume(v: number)
    @control[7] = v * 127

/**
 * MIDI channel id (0-15)
 */
type ChannelID = number

neutralNrpn := ->
  vibRate: 0
  vibDepth: 0
  vibDelay: 0
  cutoff: 0
  resonance: 0
  attack: 0
  decay: 0
  release: 0

resetControllers := (channel: Channel) ->
  {control} := channel

  // Reset any effects then restore default values
  control.fill(0)
  // Channel Volume
  control[0x07] = 0x64
  // Pan
  control[0x0A] = 0x40
  // Expression Volume
  control[0x0B] = 0x7f
  // CC 91 (reverb send) deliberately stays 0, not the GM hardware default of 40: the
  // reverb bus is always on, and a zero default is what keeps a song that never asks
  // for reverb (every Paint Composer original) sounding exactly as it did without one.

  // NRPN
  control[0x62] = 0x7f // LSB
  control[0x63] = 0x7f // MSB

  // RPN
  control[0x64] = 0x7f // LSB
  control[0x65] = 0x7f // MSB

  channel.dataMode = "rpn"
  channel.nrpn = neutralNrpn()
  channel.drumNrpn = {}

dataEntry := (handlers: Handlers, channel: Channel, t: number) ->
  {control} := channel

  if channel.dataMode is "nrpn"
    // GS sound-controller NRPNs (SC-55/88 owner's manuals): MSB 0x01, data is an offset
    // around 64 applied to the patch's own parameter. Stored on the channel; the synth
    // reads them at noteOn.
    offset := control[0x06] - 64
    switch control[0x63]
      when 0x01
        switch control[0x62]
          when 0x08 then channel.nrpn.vibRate = offset
          when 0x09 then channel.nrpn.vibDepth = offset
          when 0x0A then channel.nrpn.vibDelay = offset
          when 0x20 then channel.nrpn.cutoff = offset
          when 0x21 then channel.nrpn.resonance = offset
          when 0x63 then channel.nrpn.attack = offset
          when 0x64 then channel.nrpn.decay = offset
          when 0x66 then channel.nrpn.release = offset
      when 0x10
        // vfx shader select?
        // actually this is handled as a json string text message in the midi
        // file
        ; // TODO
      // GS drum setup, LSB = drum note number.
      when 0x18 then (channel.drumNrpn[control[0x62]] ?= {}).pitch = offset
      when 0x1a then (channel.drumNrpn[control[0x62]] ?= {}).level = control[0x06]
      when 0x1c then (channel.drumNrpn[control[0x62]] ?= {}).pan = control[0x06]
      when 0x1d then (channel.drumNrpn[control[0x62]] ?= {}).reverb = control[0x06]
      when 0x1e then (channel.drumNrpn[control[0x62]] ?= {}).chorus = control[0x06]
      when 0x1f then (channel.drumNrpn[control[0x62]] ?= {}).delay = control[0x06]
    return

  // RPN
  // https://www.midi.org/specifications-old/item/table-3-control-change-messages-data-bytes-2
  // Table 3a
  switch control[0x65]
    when 0x00
      switch control[0x64]
        when 0x00
          // Pitch bend sensitivity (cents)
          v := control[0x06] * 100 + control[0x26]
          handlers.pitchBendSensitivity(channel, v, t)
          channel.pitchBendSensitivity = v
        when 0x01
          // Channel fine tuning
          v .= (control[0x06] << 7) + control[0x26]
          v = (v - 0x2000) / 0x2000 * 100 // cents
          channel.tuningFine = v
          // fine and coarse tuning sum (RP-022); passing only one component
          // would discard the other on every update
          handlers.channelTuning(channel, channel.tuning, t)
        when 0x02
          // Channel coarse tuning
          v := (control[0x06] - 0x40) * 100 // cents
          channel.tuningCoarse = v
          handlers.channelTuning(channel, channel.tuning, t)

type Handlers = {
  /** GS effect-block config (reverb/chorus), normalized — see ChannelData.sysEx */
  fxParam?: (name: string, value: number) => void
  channelTuning: (channel: Channel, v: number, t: number) => void
  controlChange: (channel: Channel, n: number, v: number, t: number) => void
  noteOn: (channel: Channel, n: number, v: number, t: number) => void
  noteOff: (channel: Channel, n: number, v: number, t: number) => void
  pitchBend: (channel: Channel, v: number, t: number) => void
  pitchBendSensitivity: (channel: Channel, v: number, t: number) => void
  allNotesOff?: (channel: Channel, t: number) => void
  allSoundsOff?: (channel: Channel, t: number) => void
  /** The program as sent; the channel's `program` is already updated when this runs. */
  programChange?: (channel: Channel, program: number, t: number) => void
  /** Every SysEx, raw, `[0xF0, <varint length>, payload…]` — before it is decoded into fxParam. */
  sysEx?: (data: Uint8Array, t: number) => void
  /** Polyphonic key pressure (0xA0): note and pressure. Not channel state, so no snapshot. */
  keyPressure?: (channel: Channel, n: number, v: number, t: number) => void
  /** Channel pressure (0xD0). Passes through on the seek replay like a controller. */
  channelPressure?: (channel: Channel, v: number, t: number) => void
}

/**
 * ChannelData holds the state of all 16 MIDI channels and the global state
 * of the MIDI song.
 */
export class ChannelData
  beat = 0
  currentTick = 0
  t = 0
  ticksPerBeat
  microsecondsPerBeat = 500000
  /**
  Playback rate: context time advances 1/rate seconds per song second, so at
  0.5 the song plays at half speed. Transport config, not song state — it is
  not part of snapshots.
  */
  rate = 1
  channels: Channel[]
  handlers: Handlers

  @(@ticksPerBeat: number, @handlers: Handlers)
    // Store channel data
    @channels = [0...16].map (i) =>
      new Channel
        id: i
        shared: this

    // Init
    @channels.forEach resetControllers

  // https://www.midi.org/specifications-old/item/table-3-control-change-messages-data-bytes-2
  controlChange = (c: ChannelID, n: number, v: number): void =>
    channel := @channels[c]

    @handlers.controlChange?(channel, n, v, @t)
    channel.control[n] = v

    switch n
      when 0x62, 0x63 // NRPN select
        channel.dataMode = "nrpn"
      when 0x64, 0x65 // RPN select
        channel.dataMode = "rpn"
      when 0x06 // Data entry (MSB)
        dataEntry(@handlers, channel, @t)
      when 0x26 // Data entry (LSB)
        dataEntry(@handlers, channel, @t)
      when 0x60 // Data Increment
        channel.control[0x06] = (channel.control[0x06] + 1) & 0x7F
        dataEntry(@handlers, channel, @t)
      when 0x61 // Data Decrement
        channel.control[0x06] = (channel.control[0x06] - 1) & 0x7F
        dataEntry(@handlers, channel, @t)

  // c [0-15] MIDI channel id
  // n [0-127] MIDI note number
  // v [0-127] MIDI velocity
  noteOn = (c: ChannelID, n: number, v: number) =>
    @handlers.noteOn(@channels[c], n, v, @t)

  // c [0-16] MIDI channel id
  // n [0-127] MIDI note number
  // v [0-127] MIDI velocity
  noteOff = (c: ChannelID, n: number, v: number) =>
    @handlers.noteOff(@channels[c], n, v, @t)

  // v is 14-bit MIDI pitch bend value
  pitchBend = (c: ChannelID, v: number): void =>
    channel := @channels[c]
    // set channel pitch bend state
    value := pitchBendToFloat(v)
    @handlers.pitchBend(channel, value, @t)
    channel.pitchBend = value

  programChange = (c: ChannelID, p: number): void =>
    @channels[c].program = p
    @handlers.programChange?(@channels[c], p, @t)

  // GS DT1 effect-block SysEx → normalized fxParam calls. Only the system effect
  // addresses (40 01 xx) and the GS/GM resets are handled here; part params, EFX
  // insertion and drum maps stay ignored (see the engine-gaps survey). Scaling
  // constants approximate the SC-88 tables, which Roland never published exactly.
  sysEx = (data: Uint8Array): void =>
    @handlers.sysEx?(data, @t)
    // Bound, not detached: fxParam is a plain method on the synth and calling the bare
    // reference strips its `this`.
    fxParam := @handlers.fxParam?.bind @handlers
    return unless fxParam
    // data = [0xF0, <varint length>, payload…] — skip the status byte and the varint.
    i .= 1
    while data[i] & 0x80
      i++
    i++
    body := data.subarray(i)
    // GM reset: F0 7E dev 09 01 F7
    if body[0] is 0x7e and body[2] is 0x09
      return fxParam "reset", 0
    // Roland GS DT1: 41 dev 42 12 aa bb cc vv .. sum F7
    return unless body[0] is 0x41 and body[2] is 0x42 and body[3] is 0x12
    [a, b, c, v] := [body[4], body[5], body[6], body[7]]
    if a is 0x40 and b is 0x00 and c is 0x7f
      return fxParam "reset", 0
    return unless a is 0x40 and b is 0x01
    switch c
      when 0x30 // reverb macro: Room1-3, Hall1-2, Plate, Delay, Panning Delay
        // Hall2 (the power-on macro) MEASURED at ~2s RT60 on SC-55mk2; the others are
        // scaled judgments around that anchor, not measurements.
        fxParam "reverbTime", [0.7, 0.9, 1.1, 1.7, 2.0, 1.3, 0.9, 0.9][v] ?? 2.0
      when 0x33 // reverb level, 64 = the power-on default
        fxParam "reverbLevel", v / 64
      when 0x34 // reverb time, relative around 64
        fxParam "reverbTime", 2.0 * 2 ** ((v - 64) / 32)
      when 0x38 // chorus macro: Chorus1-4, FB Chorus, Flanger, Short Delay, Short Delay FB
        // Depths kept small on purpose: chorus delay modulation is pitch modulation,
        // and the first, deeper table read as out-of-tune rather than as width.
        m := [
          { rateHz: 1.1,  depthMs: 1,   feedback: 0,    delayMs: 13 }
          { rateHz: 0.8,  depthMs: 1.5, feedback: 0.05, delayMs: 15 }
          { rateHz: 0.6,  depthMs: 2.5, feedback: 0.06, delayMs: 16 }
          { rateHz: 0.9,  depthMs: 3,   feedback: 0.08, delayMs: 16 }
          { rateHz: 0.25, depthMs: 2.5, feedback: 0.4,  delayMs: 20 }
          { rateHz: 0.35, depthMs: 2,   feedback: 0.6,  delayMs: 2 }
          { rateHz: 0.05, depthMs: 0,   feedback: 0,    delayMs: 40 }
          { rateHz: 0.05, depthMs: 0,   feedback: 0.5,  delayMs: 40 }
        ][v]
        if m
          fxParam "chorusRate", m.rateHz
          fxParam "chorusDepth", m.depthMs
          fxParam "chorusFeedback", m.feedback
          fxParam "chorusDelay", m.delayMs
      when 0x3a then fxParam "chorusLevel", v / 64
      when 0x3b then fxParam "chorusFeedback", v / 127 * 0.9
      when 0x3c then fxParam "chorusDelay", v / 127 * 50
      when 0x3d then fxParam "chorusRate", 0.05 + v / 127 * 8
      when 0x3e then fxParam "chorusDepth", v / 127 * 8
      // Delay block: raw wire values through — the synth's delay bus owns the GS
      // scaling tables (time center table, v/24 ratios, level products, feedback).
      when 0x50 then fxParam "delayMacro", v
      when 0x51 then fxParam "delayPreLpf", v
      when 0x52 then fxParam "delayTimeC", v
      when 0x53 then fxParam "delayRatioL", v
      when 0x54 then fxParam "delayRatioR", v
      when 0x55 then fxParam "delayLevelC", v
      when 0x56 then fxParam "delayLevelL", v
      when 0x57 then fxParam "delayLevelR", v
      when 0x58 then fxParam "delayLevel", v
      when 0x59 then fxParam "delayFeedback", v
      when 0x5a then fxParam "delayReverbSend", v

  setTempo = (uspb: number): void =>
    // console.log "set tempo", uspb
    @microsecondsPerBeat = uspb

  /**
  CC 121 per RP-015: recenter pitch bend, modulation off, expression full,
  sustain pedal up, RPN/NRPN selection to null. Volume, pan, bank, program,
  bend range, and effect sends (CC 91/93) are deliberately NOT reset -- a
  file that sets its mix and then sends CC 121 must keep its mix.

  Pass `init: true` for the power-on reset used when constructing channel
  state: every controller back to its startup default.
  */
  resetControllers = (c: ChannelID, { init = false } = {}) =>
    if init
      resetControllers(@channels[c])
      return

    channel := @channels[c]

    // Through the normal control paths so audio follows the state: pedal-up
    // releases sustained voices, the bend recenters audibly.
    @controlChange(c, 0x40, 0)    // sustain off
    @controlChange(c, 0x01, 0)    // modulation off
    @controlChange(c, 0x0B, 0x7f) // expression full
    @pitchBend(c, 0x2000)         // center

    {control} := channel
    // RPN/NRPN selection to null so a stray data entry can't land. The GS
    // NRPN offsets themselves persist -- RP-015 resets the selection, not
    // stored parameter values.
    control[0x62] = 0x7f
    control[0x63] = 0x7f
    control[0x64] = 0x7f
    control[0x65] = 0x7f
    channel.dataMode = "rpn"

  // CC 123-127: release sounding notes on the channel (normal release phase)
  allNotesOff = (c: ChannelID) =>
    @handlers.allNotesOff?(@channels[c], @t)

  // CC 120: silence the channel immediately, no release
  allSoundsOff = (c: ChannelID) =>
    @handlers.allSoundsOff?(@channels[c], @t)

  restoreSnapshot(s: ReturnType<typeof @stateSnapshot>)
    @beat = s.beat
    @currentTick = s.currentTick
    @microsecondsPerBeat = s.microsecondsPerBeat
    @ticksPerBeat = s.ticksPerBeat
    @channels.forEach (channel, i) ->
      snap := s.channels[i]

      channel.control = snap.control.slice()
      channel.pitchBend = snap.pitchBend
      channel.pitchBendSensitivity = snap.pitchBendSensitivity
      channel.program = snap.program
      channel.tuningCoarse = snap.tuningCoarse
      channel.tuningFine = snap.tuningFine
      channel.dataMode = snap.dataMode
      channel.nrpn = {...snap.nrpn}
      channel.drumNrpn = Object.fromEntries Object.entries(snap.drumNrpn).map ([k, v]) -> [k, {...v}]

  stateSnapshot()
    beat: @beat
    channels: @channels.map ({
      id
      control
      dataMode
      nrpn
      drumNrpn
      program
      pitchBend
      pitchBendSensitivity
      tuningCoarse
      tuningFine
    }) =>
      {
        id
        control: control.slice()
        dataMode
        nrpn: {...nrpn}
        drumNrpn: Object.fromEntries Object.entries(drumNrpn).map ([k, v]) -> [k, {...v}]
        pitchBend
        pitchBendSensitivity
        program
        tuningCoarse
        tuningFine
      }
    currentTick: @currentTick
    microsecondsPerBeat: @microsecondsPerBeat
    ticksPerBeat: @ticksPerBeat

  // sync the current "clock" to the audio context
  // @ts-ignore TODO: global context
  sync(contextTime=context.currentTime)
    @t = contextTime

  /**
  Convert a delta time into seconds based on the current tempo and the
  configured ticksPerBeat
  */
  deltaTimeToSeconds(deltaTime: number)
    deltaTime / @ticksPerBeat * @microsecondsPerBeat / microsecondsPerSecond

  tick = (tick: number) =>
    deltaTime := tick - @currentTick
    @currentTick = tick
    beats := deltaTime / @ticksPerBeat
    @beat += beats // song beat (beats)
    s := beats * @microsecondsPerBeat / microsecondsPerSecond
    @t += s / @rate // context time (seconds)

  handleEvent = (e: MidiEvent): void =>
    { tick, type } := e
    @tick(tick)

    switch type
      when "noteOn"
        @noteOn(e.c, e.n, e.v)
      when "noteOff"
        // A zero-velocity noteOn is a noteOff at the GM default release
        // velocity 0x40 (the pre-refactor MidiEventHandler behavior)
        v := if (e.typeByte >> 4) is 0x9 and e.v is 0 then 0x40 else e.v
        @noteOff(e.c, e.n, v)
      when "controlChange"
        @controlChange(e.c, e.n, e.v)
      when "resetControllers"
        @resetControllers(e.c)
      when "allNotesOff"
        @allNotesOff(e.c)
        // CC 124–127 (omni off/on, mono, poly) are mode changes that imply all notes
        // off; the mode itself goes to the synth as the controller it is.
        @handlers.controlChange(@channels[e.c], e.n, e.v, @t) if e.n >= 124
      when "allSoundsOff"
        @allSoundsOff(e.c)
      when "sysEx"
        @sysEx(e.data)
      when "pitchBend"
        // e.v is only the MSB — bends need the full 14-bit value or every
        // event reads as nearly full-down (v/8192 - 1 with v ≤ 127).
        @pitchBend(e.c, e.bend)
      when "programChange"
        @programChange(e.c, e.program)
      when "keyPressure"
        @handlers.keyPressure?(@channels[e.c], e.n, e.v, @t)
      when "channelPressure"
        // One data byte: the pressure sits where a note number would.
        @handlers.channelPressure?(@channels[e.c], e.n, @t)
      when "setTempo"
        @setTempo(e.microsecondsPerBeat)

export { Channel as MidiChannel }
